import { useEffect, useMemo, useRef, useState } from "react";
import { PhotoCard } from "./PhotoCard";

const ratio = (p) => { const v = p.variants?.medium; return v ? v.width / v.height : 1.5; };

const useWidth = () => {
  const ref = useRef(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    if (!ref.current) return undefined;
    const ro = new ResizeObserver(([e]) => setW(e.contentRect.width));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
};

// Justified rows: keeps your exact order left-to-right and every photo's natural proportions
export const JustifiedGrid = ({ photos, targetHeight = 380, gap = 12, testid = "photo-grid" }) => {
  const [ref, width] = useWidth();
  const rows = useMemo(() => {
    if (!width) return [];
    const target = width < 640 ? Math.max(220, targetHeight * 0.65) : targetHeight;
    const out = [];
    let row = [];
    const flush = (cap) => {
      if (!row.length) return;
      const sum = row.reduce((a, x) => a + ratio(x.p), 0);
      const h = (width - gap * (row.length - 1)) / sum;
      out.push({ items: row, h: cap ? Math.min(h, cap) : h });
      row = [];
    };
    photos.forEach((p, i) => {
      if (p.wide || (width < 640 && ratio(p) > 1.2)) { flush(target * 1.15); row = [{ p, i }]; flush(width < 640 ? 0 : target * 1.9); return; }
      row.push({ p, i });
      const sum = row.reduce((a, x) => a + ratio(x.p), 0);
      if ((width - gap * (row.length - 1)) / sum <= target) flush(0);
    });
    flush(target * 1.15);
    return out;
  }, [photos, width, targetHeight, gap]);
  return (
    <div ref={ref} data-testid={testid} className="flex flex-col" style={{ gap }}>
      {rows.map((r, k) => (
        <div key={k} className="flex justify-center" style={{ gap, height: r.h }}>
          {r.items.map(({ p, i }) => (
            <div key={p.id} style={{ width: r.h * ratio(p), flexShrink: 0 }} className="h-full">
              <PhotoCard photo={p} list={photos} index={i} className="h-full" sizes={`${Math.ceil((r.h * ratio(p)) / Math.max(width, 1) * 100)}vw`} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

const ASPECT = { "3/2": 1.5, "4/5": 0.8, "1/1": 1 };
const COLS = { 2: "sm:grid-cols-2", 3: "sm:grid-cols-2 lg:grid-cols-3", 4: "sm:grid-cols-2 lg:grid-cols-4" };

export const UniformGrid = ({ photos, columns = 3, aspect = "3/2", testid = "photo-grid" }) => (
  <div data-testid={testid} className={`grid grid-cols-1 gap-3 ${COLS[columns] || COLS[3]}`}>
    {photos.map((p, i) => {
      const a = ASPECT[aspect] || 1.5;
      return (
        <div key={p.id} className={p.wide ? "sm:col-span-2" : ""} style={{ aspectRatio: p.wide ? `${a * 2} / 1` : `${a} / 1` }}>
          <PhotoCard photo={p} list={photos} index={i} className="h-full" sizes={p.wide ? "66vw" : `${Math.round(100 / columns)}vw`} />
        </div>
      );
    })}
  </div>
);

export const GalleryGrid = ({ photos, layout }) => {
  const l = layout || {};
  if (l.layout === "grid") return <UniformGrid photos={photos} columns={Number(l.columns) || 3} aspect={l.aspect || "3/2"} />;
  return <JustifiedGrid photos={photos} targetHeight={{ s: 280, m: 380, l: 500 }[l.rowSize || "m"]} />;
};
